import { useIntl } from 'react-intl';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import type {
  GallerySortName,
  GallerySortNames,
} from '@sorare/core/src/contexts/config';
import { GQLSearchContext } from '@sorare/core/src/contexts/graphql-search';
import { SearchCardsProvider as SearchCardsContextProvider } from '@sorare/core/src/contexts/searchCards/Provider';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import type { FiltersGroup } from '@sorare/core/src/lib/filters';

import { BackendMarketSearchLayout } from '#src/components/market/BackendMarketSearchLayout/index.tsx';
import { CurrentMarketplaceUserProvider } from '#src/contexts/currentMarketplaceUser/Provider.tsx';
import { useCurrentMarketplaceUserContext } from '#src/contexts/currentMarketplaceUser/index.ts';
import { useBackendMarketCardsSearch } from '#src/hooks/search/useBackendMarketCardsSearch.ts';
import { MARKET_CARDS_SEARCH_QUERY } from '#src/hooks/search/useMarketCardsSearch.ts';
import { messages as searchLayoutMessages } from '#src/searchCards/AdvancedCardSearch/SearchLayout.tsx';
import { BackendCardResultsFromGraphQL } from '#src/searchCards/BackendCardResultsFromGraphQL/index.tsx';

const placeholderMessages = {
  player: searchLayoutMessages.playerPlaceholder,
  team: searchLayoutMessages.teamPlaceholder,
  league: searchLayoutMessages.leaguePlaceholder,
  country: searchLayoutMessages.countryPlaceholder,
};

type Props = {
  sport: Sport;
  cardFilters: FiltersGroup[];
  defaultFilters: string[];
  sorts: GallerySortNames;
  defaultSort: GallerySortName;
  topic: {
    type: 'player' | 'team' | 'league' | 'country';
    label: string;
  };
};

const BackendCardPageSearchContent = ({
  sport,
  cardFilters,
  defaultFilters,
  sorts,
  defaultSort,
  topic,
}: Props) => {
  const { formatMessage } = useIntl();

  const {
    query,
    page,
    refinements,
    facets,
    sortInputs,
    advancedFilters,
    filtersLoading,
    makeGqlSearchContextValue,
  } = useBackendMarketCardsSearch({
    sport,
    cardFilters,
    defaultFilters,
    defaultSort,
    onSaleOnly: false,
  });

  const { data, loading, previousData } = useQuery(MARKET_CARDS_SEARCH_QUERY, {
    skip: filtersLoading,
    variables: {
      query,
      page,
      pageSize: 20,
      advancedFilters,
      sorts: sortInputs,
      refinements,
      facets,
      onSaleOnly: false,
    },
  });

  const safeData = data ?? previousData;
  const searchCards = safeData?.searchCards;
  const hitSlugs = searchCards?.hits?.map(h => h.slug) ?? [];

  return (
    <GQLSearchContext value={makeGqlSearchContextValue(searchCards)}>
      <BackendMarketSearchLayout
        cardFilters={cardFilters}
        sorts={sorts}
        searchBoxPlaceholder={formatMessage(placeholderMessages[topic.type], {
          name: topic.label,
        })}
      >
        <BackendCardResultsFromGraphQL
          hitSlugs={hitSlugs}
          searchLoading={loading || filtersLoading}
          nbHits={searchCards?.nbHits ?? 0}
          sport={sport}
          topic={topic}
          showDesktopFilter
        />
      </BackendMarketSearchLayout>
    </GQLSearchContext>
  );
};

export const BackendCardPageSearch = ({ sport, ...props }: Props) => {
  const currentMarketplaceUserContext = useCurrentMarketplaceUserContext();

  return (
    <ConditionalWrapper
      wrap={!currentMarketplaceUserContext}
      Wrapper={CurrentMarketplaceUserProvider}
      props={{ sport }}
    >
      <SearchCardsContextProvider commonMarket={false}>
        <BackendCardPageSearchContent sport={sport} {...props} />
      </SearchCardsContextProvider>
    </ConditionalWrapper>
  );
};
